// The show editor's two searches, shared (ART-330): the VENUE (Google Places — the place id, town and coordinates the
// show links to) and the ACT (friends, then any band on Artist CNTRL, then the Spotify catalogue). Lifted out of
// `ShowWizard` so the agent's "Send to team" editor searches the same database and a request's venue and lineup
// land on the hold exactly as the editor would have written them.
import type { components } from "@cntrl/api-client";
import { useVenueAutocomplete, useArtistAutocomplete } from "../../app/hooks/useShowLookup.js";
import { useActBandSearch } from "../../app/hooks/useShows.js";
import { useFriends } from "../../app/hooks/useFriends.js";
import type { TypeaheadSuggestions } from "./TypeaheadInput.js";

export type VenueSuggestion = components["schemas"]["VenueSuggestion"];
type ArtistSuggestion = components["schemas"]["ArtistSuggestion"];

/** Google Places venue suggestions adapted to the typeahead contract — `available:false` (vendor degraded) leaves
 * the field a plain-text input (defaults to true while the short/disabled query returns nothing). */
export function useVenueSuggestions(q: string): TypeaheadSuggestions<VenueSuggestion> {
  const r = useVenueAutocomplete(q);
  return { items: r.data?.items ?? [], available: r.data?.available ?? true };
}

/**
 * An act the lineup can add: a band ON ARTIST CNTRL (a friend, or any other registered band the name search
 * resolved), or a Spotify catalogue artist, or free text.
 *
 * A CNTRL band carries a `friendBandId`, and that id is the whole point — `ShowBillAct.onArtistCntrl` is
 * `friend_band_id != null`, so picking one is what lights the AC mark on the bill line, and it resolves by ID
 * rather than by name (matching an act to a band by string is legacy's `friendByName`, which
 * `ShowLineupSlot.cs` refuses to port). Since ART-165 the mark is NOT friends-only: any registered band is
 * "on Artist CNTRL" (the name column doubling as the platform-band link the entity always intended), while
 * friendship still gates SHARING the show — a mark on a bill and an invite you can send are two questions.
 * A Spotify artist is a real act with artwork but no CNTRL account, and free text is neither — both stay
 * unmarked, correctly.
 */
export type ActSuggestion = {
  name: string;
  spotifyArtistId: string | null;
  imageUrl: string | null;
  /** Set for any registered CNTRL band — the AC-mark predicate. */
  friendBandId: string | null;
  /** A CNTRL band's picture id, so the suggestion row can draw its photo through the global avatar route
   *  (`imageUrl` is Spotify artwork and is always null for a CNTRL band). */
  avatarMediaId: string | null;
  /** An ACTIVE friend of ours. Friends on the lineup are auto-invited to the show, so the suggestion row badges
   *  them "Friend" (ART-310) — the useful signal at pick time, beside the AC mark every CNTRL band carries. */
  isFriend: boolean;
};

/** Lineup act suggestions: CNTRL bands (friends + the registered-band name search) then Spotify then free text. */
export function useArtistSuggestions(q: string): TypeaheadSuggestions<ActSuggestion> {
  const r = useArtistAutocomplete(q);
  const bandSearch = useActBandSearch(q);
  const friends = useFriends();
  const query = q.trim().toLowerCase();

  // ACTIVE friendships only. An incoming or outgoing REQUEST is not yet a band you can put on a bill and
  // truthfully mark as connected. Friends come FIRST because they are the acts you can also SHARE the show
  // with — the mark plus the `+` on the preview — not merely mark.
  const friendItems: ActSuggestion[] = (friends.data?.active ?? [])
    .filter((f: FriendSummary) => (f.bandName ?? "").toLowerCase().includes(query))
    .map((f: FriendSummary) => ({
      name: f.bandName ?? "",
      spotifyArtistId: null,
      imageUrl: null,
      friendBandId: f.friendBandId ?? null,
      avatarMediaId: f.avatarMediaId ?? null,
      isFriend: true,
    }));

  // Every OTHER registered band the name search returned — marked, but not a friend (so not shareable). The
  // server already excludes our own band; we drop any that is also a friend so a band is never listed twice.
  const friendBandIds = new Set(friendItems.map((f) => f.friendBandId));
  const searchItems: ActSuggestion[] = (bandSearch.data?.items ?? [])
    .filter((b) => b.bandId != null && !friendBandIds.has(b.bandId))
    .map((b) => ({
      name: b.name ?? "",
      spotifyArtistId: null,
      imageUrl: null,
      friendBandId: b.bandId ?? null,
      avatarMediaId: b.avatarMediaId ?? null,
      isFriend: false,
    }));

  const spotifyItems: ActSuggestion[] = (r.data?.items ?? []).map((a: ArtistSuggestion) => ({
    name: a.name ?? "",
    spotifyArtistId: a.spotifyArtistId ?? null,
    imageUrl: a.imageUrl ?? null,
    friendBandId: null,
    avatarMediaId: null,
    isFriend: false,
  }));

  // CNTRL bands first: they are the acts we can say something TRUE about. A Spotify hit with the same name
  // as a band already offered is dropped rather than listed twice — one of the two would carry the mark and
  // the other would not, which reads as a bug whichever one you pick.
  const seen = new Set([...friendItems, ...searchItems].map((f) => f.name.toLowerCase()));
  return {
    items: [...friendItems, ...searchItems, ...spotifyItems.filter((b) => !seen.has(b.name.toLowerCase()))],
    // The non-vendor sources need no vendor at all, so the picker only degrades to plain text when Spotify is
    // unavailable AND there is no CNTRL band to offer either.
    available: (r.data?.available ?? true) || friendItems.length > 0 || searchItems.length > 0,
  };
}

type FriendSummary = components["schemas"]["FriendSummary"];
export function venueMeta(v: VenueSuggestion): string {
  return [v.city, v.state, v.country].filter(Boolean).join(", ");
}
